import { Directive, EventEmitter, HostBinding, HostListener, Output, Input } from '@angular/core';

@Directive({
  selector: '[appDragDrop]',
})
export class DragDropDirective {

  @Input() dragData: any;
  @Input() disableDrop = false;
  @Input() dragDisabled = false;
  @Output() dataDropped = new EventEmitter<any>();
  @Output() fileDropped = new EventEmitter<File[]>();

  // 🔥 state flags
  isDragging = false;      // hovering over drop zone
  isDragSource = false;   // element being dragged
  isDropTarget = false;   // can accept drop (optional)

  // ✅ bind classes
  @HostBinding('class.drag-over') get dragOverClass() { return this.isDragging; }
  @HostBinding('class.drag-source') get dragSourceClass() { return this.isDragSource; }
  @HostBinding('class.drop-target') get dropTargetClass() { return this.isDropTarget; }



  // ✅ make draggable automatically if it has data
  @HostBinding('attr.draggable')
  get draggableAttr() {
    if (this.dragDisabled) return null;
    return this.dragData ? true : null;
  }

  // -----------------------
  // DRAG START
  // -----------------------
  @HostListener('dragstart', ['$event'])
  onDragStart(event: DragEvent) {

    if (this.dragDisabled) {
      event.preventDefault();
      return;
    }

    this.isDragSource = true;

    if (this.dragData) {
      event.dataTransfer?.setData('application/json', JSON.stringify(this.dragData));
      event.dataTransfer!.effectAllowed = 'move';
    }
  }

  // -----------------------
  // DRAG END
  // -----------------------
  @HostListener('dragend')
  onDragEnd() {
    this.isDragSource = false;
  }

  // -----------------------
  // DRAG OVER (drop zone)
  // -----------------------
  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent) {
    if (this.disableDrop || this.dragDisabled) return;
    event.preventDefault();
    event.stopPropagation();

    this.isDragging = true;
    this.isDropTarget = true;

    event.dataTransfer!.dropEffect = 'move';
  }

  // -----------------------
  // DRAG LEAVE
  // -----------------------
  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.isDragging = false;
    this.isDropTarget = false;
  }

  // -----------------------
  // DROP
  // -----------------------
  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent) {
    if (this.disableDrop || this.dragDisabled) return;
    event.preventDefault();
    event.stopPropagation();

    this.isDragging = false;
    this.isDropTarget = false;

    // ✅ FILES
    if (event.dataTransfer?.files?.length) {
      this.fileDropped.emit(Array.from(event.dataTransfer.files));
      return;
    }

    // ✅ DATA
    const raw = event.dataTransfer?.getData('application/json');

    if (raw) {
      try {
        const data = JSON.parse(raw);
        this.dataDropped.emit(data);
      } catch (e) {
        console.warn('DragDrop parse failed', e);
      }
    }
  }
}
